// Contract drilling (DESIGN §17): rent your rig + crew to AI operators by the day, the foot, or the job.
//
// State (added lazily by initContracts; survives JSON round-trips):
//   state.contracts = {
//     board: [Offer], nextRefreshDay, lastRefreshDay, seq,
//     active: { [contractId]: Contract },
//     operators: { [opId]: { id, name, short, regionId, major, satisfaction, completed, failed, wells, sinceDay } },
//     tips: [Tip], history: [HistoryRow], stats: { completed, failed, abandoned, earned, penalties }
//   }
// Contract wells are ordinary Well records (created with drilling.createWell) carrying
//   well.ownerOperatorId / well.contractId, so they run through the normal drilling sim
//   (WOB, bits, casing, accidents, fishing). At TD they are handed over (status 'HANDED_OVER')
//   and drained by the operator like a rival's well.
// All randomness goes through src/core/rng.js.
import { allWells } from './indexes.js'; // perf: per-day well list (same order as Object.values)
import { nextFloat, chance, range, int, pick } from '../core/rng.js';
import { dateToDay, dayToDate } from '../core/time.js';
import { eqDef, fieldDef, rigStats, bitStats, stat, unitPrice, eraDollars, defsByCat, isAvailable, yearOf, marketWage } from './defs.js';
import { strataAt, reservoirsAt } from './geo.js';
import { createWell, startRigUp, releaseRigImpl, ropAt, bestSpareBit, ACTIVE_STATUSES, HARD_TUNE } from './drilling.js';
import { cancelTimer } from './timers.js';
import { activeRigCrew, crewWage } from './crew.js';
import { fieldOpen, leaseImpl, leasePrice } from './survey.js';
import { clamp, notify, log, spend, earn, book, fmt$ } from './util.js';
import { rushCost } from '../economy/tokens.js';
import { eraForDay } from '../data/eras.js';
import { generateOperatorName, majorsFor, operatorWeight, BOARD_FLAVOR } from '../data/operators.js';

// ---- tuning ---------------------------------------------------------------------------------
export const CONTRACTS_OPEN_DAY = dateToDay(1860, 3, 1); // "Lease Fever on Oil Creek" — the first contract drillers
export const BOARD_DAYS = 14;
export const TURNKEY_MIN_REP = 40;
export const TIP_CHANCE_MIN = 0.10, TIP_CHANCE_MAX = 0.20;
export const TIP_DISCOUNT = 0.4;
export const TIP_DAYS = 45;
export const MIN_OPERATORS = 5, MAX_OPERATORS = 16;
export const LATE_GRACE = 0.5;          // contract fails at deadline + 50% of the allowed days
export const REF_EFFICIENCY = 0.6;      // crew/bit/condition/overhead factor for the operators' reference estimate
export const HANDOVER_KEEP_DAYS = 3650; // handed-over wells keep draining their reservoir this long
export const TERMS = ['dayrate', 'footage', 'turnkey'];
export const TERM_LABEL = { dayrate: 'Day rate', footage: 'Footage', turnkey: 'Turnkey' };
const DONE_STATUSES = new Set(['AT_TD', 'DRY', 'COMPLETING', 'PRODUCING', 'SHUT_IN', 'DEPLETED']);

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });

// ---- state ----------------------------------------------------------------------------------
export function initContracts(state) {
  const C = (state.contracts ??= {});
  C.board ??= [];
  C.active ??= {};
  C.operators ??= {};
  C.tips ??= [];
  C.history ??= [];
  C.stats ??= {};
  for (const k of ['completed', 'failed', 'abandoned', 'earned', 'penalties']) C.stats[k] ??= 0;
  C.nextRefreshDay ??= Math.max(state.day, CONTRACTS_OPEN_DAY);
  C.lastRefreshDay ??= null;
  C.seq ??= 1;
  return C;
}

const nid = (C, p) => `${p}${C.seq++}`;
const isFree = (p) => p && !p.owned && !p.wellId && !p.taken;
// perf: refreshBoard posts ~6 offers and each re-scanned every field's plots several times. Inside a refresh nothing
// changes plots or field openings, so the scans are shared (BOARD_BATCH); outside one every call is fresh.
let BOARD_BATCH = null;
export function freePlots(state, fieldId) {
  if (BOARD_BATCH?.state === state) { let v = BOARD_BATCH.free.get(fieldId); if (!v) BOARD_BATCH.free.set(fieldId, (v = freePlotsRaw(state, fieldId))); return v; }
  return freePlotsRaw(state, fieldId);
}
function freePlotsRaw(state, fieldId) {
  const f = state.fields[fieldId];
  if (!f) return [];
  const out = [];
  f.plots.forEach((p, i) => { if (isFree(p)) out.push(i); });
  return out;
}
const openFieldIdsRaw = (state) => Object.keys(state.fields).filter((fid) => fieldDef(fid) && fieldOpen(state, fid));
const openFieldIds = (state) => (BOARD_BATCH?.state === state ? (BOARD_BATCH.open ??= openFieldIdsRaw(state)) : openFieldIdsRaw(state));
const rand = (state) => () => nextFloat(state);
const round = (x, step) => Math.max(step, Math.round(x / step) * step);
// Round money to something an 1860s clerk (or a 2020s PDF) would write.
export function niceMoney(x) {
  const a = Math.abs(x);
  const step = a < 2 ? 0.05 : a < 10 ? 0.25 : a < 100 ? 1 : a < 1000 ? 5 : a < 10000 ? 50 : a < 1e5 ? 250 : a < 1e6 ? 1000 : 10 ** (Math.floor(Math.log10(a)) - 2);
  return Math.round(x / step) * step;
}

// ---- reference economics (operators price offers off an era-typical rig, not yours) ----------
function landRigDefs(state) {
  return defsByCat('rig').filter((d) => isAvailable(d, state) && !stat(d, 'offshore', false) && !stat(d, 'space', false));
}
function bestEngine(state) {
  const e = defsByCat('engine').filter((d) => isAvailable(d, state) && stat(d, 'powerMult', 0) > 0.6);
  e.sort((a, b) => stat(b, 'powerMult', 0) - stat(a, 'powerMult', 0));
  return e[0] ?? null;
}
function refBitLife(state, rigDef) {
  const rt = rigStats(rigDef).rigType;
  const bits = defsByCat('bit').filter((d) => isAvailable(d, state) && (!d.rigTypes || d.rigTypes.includes(rt)));
  return bits.length ? Math.max(...bits.map((d) => bitStats(d).life)) : 300;
}
function refPower(state, rigDef) {
  const rs = rigStats(rigDef);
  if (!rs.needsEngine) return rs.powerMult ?? 0.6;
  return stat(bestEngine(state), 'powerMult', 1);
}
export function refDailyCost(state, rigDef) {
  const rs = rigStats(rigDef);
  const y = yearOf(state);
  let c = 0;
  for (const [role, n] of Object.entries(rs.requiredRoles ?? {})) c += n * marketWage(role, y, 50) * 1.5; // two tours
  if (rs.needsEngine) {
    const e = bestEngine(state);
    if (e) c += eraDollars(state, stat(e, 'fuelPerDay', 0), e.baseYear ?? e.year ?? 1859);
  }
  return c;
}
// Rough casing spend for a well of this depth (half the hole cased with the cheapest available pipe).
export function consumablesEstimate(state, depth) {
  const defs = defsByCat('casing').filter((d) => isAvailable(d, state) && !stat(d, 'perWell', false) && !stat(d, 'perJob', false));
  if (!defs.length) return 0;
  const ppf = Math.min(...defs.map((d) => unitPrice(state, d)));
  return ppf * depth * 0.5;
}

// Drill-time projection through the strata to `target`. ropFn(stratum) -> ft/day.
// Adds bit service (dress/trip ~1.5 d per 85% of bit life) and rig-up days.
export function projectDrill(strata, target, ropFn, { bitLife = 300, rigUpDays = 2 } = {}) {
  let days = 0, wear = 0, ft = 0;
  for (const s of strata) {
    if (s.top >= target) break;
    const seg = Math.min(s.bottom, target) - Math.max(s.top, 0);
    if (seg <= 0) continue;
    const r = ropFn(s);
    if (!(r > 1e-6)) return { days: Infinity, ft: 0, avgRop: 0 };
    days += seg / r;
    wear += seg * Math.max(1, s.hard ?? 3) / bitLife * 100;
    ft += seg;
  }
  if (ft < target - 1e-6) {
    // below the deepest defined stratum: extend the last one
    const last = strata[strata.length - 1];
    const r = last ? ropFn(last) : 0;
    if (!(r > 1e-6)) return { days: Infinity, ft, avgRop: 0 };
    const seg = target - ft;
    days += seg / r; wear += seg * Math.max(1, last.hard ?? 3) / bitLife * 100; ft = target;
  }
  const drillDays = days;
  days += (wear / 85) * 1.5 + rigUpDays;
  return { days: Math.max(1, days), ft, avgRop: drillDays > 0 ? ft / drillDays : 0 };
}

function refProjection(state, rigDef, strata, target) {
  const rs = rigStats(rigDef);
  const power = refPower(state, rigDef);
  const ropFn = (s) => rs.baseRop * power * (HARD_TUNE / (1 + Math.max(1, s.hard ?? 3))) * REF_EFFICIENCY;
  return projectDrill(strata, target, ropFn, { bitLife: refBitLife(state, rigDef), rigUpDays: rs.rigUpDays });
}

// ---- pure term maths (also used by the UI & tests) ------------------------------------------
// Gross value of an offer if drilled in `days` with `ft` feet made and `consumables` spent.
// Past the grace period the operator terminates: day rate stops at the deadline, footage pays only the
// feet made by then, turnkey pays nothing — and the full penalty is charged.
export function contractValue(offer, { days, ft = offer.targetDepth, consumables = 0 } = {}) {
  const killDay = offer.deadlineDays + Math.ceil(offer.deadlineDays * LATE_GRACE);
  if (days > killDay) {
    let v = 0;
    if (offer.terms === 'dayrate') v = offer.rate * offer.deadlineDays + Math.min(offer.consumablesCap ?? 0, consumables * killDay / days);
    else if (offer.terms === 'footage') v = offer.rate * Math.min(ft, offer.targetDepth) * (killDay / days);
    return v - offer.penalty;
  }
  let v = 0;
  if (offer.terms === 'dayrate') v = offer.rate * Math.min(days, offer.deadlineDays) + Math.min(offer.consumablesCap ?? 0, consumables);
  else if (offer.terms === 'footage') v = offer.rate * Math.min(ft, offer.targetDepth);
  else v = offer.rate;
  if (days <= offer.earlyDays) v += offer.bonus;
  const late = Math.max(0, Math.ceil(days - offer.deadlineDays));
  v -= Math.min(offer.penalty, late * offer.latePerDay);
  return v;
}

// Settlement at TD: bonus, late penalty.
export function settleTerms(offer, { day, startDay, deadlineDay }) {
  const lateDays = Math.max(0, day - deadlineDay);
  const early = lateDays === 0 && day - startDay <= offer.earlyDays;
  return { lateDays, early, bonus: early ? offer.bonus : 0, latePenalty: Math.min(offer.penalty, lateDays * offer.latePerDay) };
}

// Satisfaction change for a finished contract (before clamping).
export function satisfactionDelta({ early = false, lateDays = 0, struck = false, incidents = 0, deaths = 0 } = {}) {
  return 8 + (early ? 10 : 0) - Math.min(25, lateDays * 1.5) + (struck ? 6 : 0) - 4 * incidents - 15 * deaths;
}

export const satisfactionRateMult = (sat) => 0.9 + 0.25 * clamp(sat ?? 50, 0, 100) / 100;
export const repRateMult = (rep) => 0.9 + 0.3 * clamp(rep ?? 30, 0, 100) / 100;
export const isRepeatClient = (op) => !!op && (op.completed ?? 0) >= 1 && (op.satisfaction ?? 0) >= 70;

export function describeTerms(o) {
  if (o.terms === 'dayrate') return `${fmt$(o.rate)}/day${o.consumablesCap ? ` + consumables to ${fmt$(o.consumablesCap)}` : ''}`;
  if (o.terms === 'footage') return `${fmt$(o.rate)}/ft`;
  return `${fmt$(o.rate)} on reaching TD`;
}

// ---- operators ------------------------------------------------------------------------------
function regionWeights(state) {
  if (BOARD_BATCH?.state === state) return (BOARD_BATCH.rw ??= regionWeightsRaw(state));
  return regionWeightsRaw(state);
}
function regionWeightsRaw(state) {
  const w = {};
  for (const fid of openFieldIds(state)) {
    const r = fieldDef(fid).regionId;
    w[r] = (w[r] ?? 0) + (r === 'pennsylvania' ? 3 : 1) * (freePlots(state, fid).length ? 1 : 0.1);
  }
  return w;
}
function weightedPick(state, items, weightFn) {
  const ws = items.map(weightFn);
  const tot = ws.reduce((a, b) => a + b, 0);
  if (!(tot > 0)) return items[0];
  let x = nextFloat(state) * tot;
  for (let i = 0; i < items.length; i++) { x -= ws[i]; if (x <= 0) return items[i]; }
  return items[items.length - 1];
}

export function addOperator(state, { regionId = null, major = null } = {}) {
  const C = state.contracts;
  const y = yearOf(state);
  if (!regionId) {
    const rw = regionWeights(state);
    const regions = Object.keys(rw);
    regionId = regions.length ? weightedPick(state, regions, (r) => rw[r]) : 'pennsylvania';
  }
  let name, short;
  if (major) ({ name, short } = major);
  else {
    const taken = new Set(Object.values(C.operators).map((o) => o.name));
    for (let i = 0; i < 6; i++) { ({ name, short } = generateOperatorName(rand(state), { year: y, regionId })); if (!taken.has(name)) break; }
  }
  const id = major ? `op_${major.id}` : nid(C, 'op');
  C.operators[id] = { id, name, short, regionId, major: !!major, satisfaction: major ? 45 : 50, completed: 0, failed: 0, wells: 0, sinceDay: state.day };
  return C.operators[id];
}

function ensureOperators(state) {
  const C = state.contracts;
  const y = yearOf(state);
  const live = () => Object.keys(C.operators).length;
  while (live() < MIN_OPERATORS) addOperator(state);
  if (live() < MAX_OPERATORS && chance(state, 0.25)) addOperator(state);
  for (const r of Object.keys(regionWeights(state))) {
    for (const m of majorsFor(y, r)) if (!C.operators[`op_${m.id}`] && live() < MAX_OPERATORS + 4) addOperator(state, { regionId: r, major: m });
  }
}

// ---- board ----------------------------------------------------------------------------------
export function boardSize(state) {
  const rep = state.company.reputation ?? 30;
  const types = new Set(Object.values(state.rigs).map((r) => r.defId)).size;
  return clamp(2 + Math.floor(rep / 20) + Math.min(2, types), 2, 7);
}

// Candidate target depths for a plot: the sands the operator is after (bottom of each pay), or a
// stratigraphic guess for wildcat plots outside any prospect.
function targetCandidates(fdef, plot) {
  const own = reservoirsAt(fdef, plot).map((r) => r.top + r.thick * 0.6);
  if (own.length) return own;
  const m = plot.truth?.depthMult ?? 1;
  const out = [];
  for (const p of fdef.prospects ?? []) for (const r of p.reservoirs ?? []) out.push((r.top + (r.dipFtPerPlot ?? 0) * 1.5 + r.thick * 0.6) * m);
  if (!out.length) for (const s of fdef.strata ?? []) out.push(s.bottom * m);
  return out;
}

export function makeOffer(state, { fitDef = null, operator = null, fieldIds = null } = {}) {
  const C = state.contracts;
  const year = yearOf(state);
  const ops = Object.values(C.operators);
  const rw = regionWeights(state);
  const op = operator ?? weightedPick(state, ops, (o) => operatorWeight(o) * (rw[o.regionId] ? 1 : 0.05));
  if (!op) return null;
  // field: in the operator's region if possible
  // land offers stay on land (offshore work comes as charters, which pass their own fieldIds)
  let fids = (fieldIds ?? openFieldIds(state).filter((fid) => !((fieldDef(fid)?.waterDepthFt ?? 0) > 0))).filter((fid) => freePlots(state, fid).length);
  if (!fids.length) return null;
  const home = fids.filter((fid) => fieldDef(fid).regionId === op.regionId);
  if (home.length) fids = home;
  // operators drill where the action is: fields opened in the last ~60 years get most of the work
  // (old shallow fields like Oil Creek still turn up now and then)
  const recency = (fid) => { const oy = 1859 + (fieldDef(fid)?.opensDay ?? 0) / 365.25; return 0.12 + 3 * clamp((oy - (year - 70)) / 70, 0, 1); };
  const fieldId = weightedPick(state, fids, recency);
  const fdef = fieldDef(fieldId);
  const f = state.fields[fieldId];
  const plots = freePlots(state, fieldId);
  const plotIdx = weightedPick(state, plots, (i) => 0.2 + (f.plots[i].pubEst ?? 0.3));
  const plot = f.plots[plotIdx];

  // what rig class the job is sized for
  const defs = landRigDefs(state);
  if (!defs.length) return null;
  const capDef = fitDef ?? weightedPick(state, defs, (d) => 1 + (d.year ?? 1859) - 1858);
  const cap = rigStats(capDef).maxDepth;
  const cands = targetCandidates(fdef, plot).filter((d) => d <= cap * 0.97 && d > 15);
  const target = Math.round((cands.length ? pick(state, cands) : cap * range(state, 0.45, 0.9)) / 5) * 5;
  if (target < 20) return null;
  // requirements
  let rigType = null;
  if (!fitDef && year >= 1901 && chance(state, 0.35) && defs.some((d) => rigStats(d).rigType === 'rotary' && rigStats(d).maxDepth >= target)) rigType = 'rotary';
  else if (fitDef && chance(state, 0.3)) rigType = rigStats(fitDef).rigType;
  const refDef = [...defs].filter((d) => rigStats(d).maxDepth >= target && (!rigType || rigStats(d).rigType === rigType))
    .sort((a, b) => (a.year ?? 0) - (b.year ?? 0) || rigStats(a).maxDepth - rigStats(b).maxDepth)[0] ?? capDef;

  // reference economics
  const strata = strataAt(fdef, plot);
  const proj = refProjection(state, refDef, strata, target);
  if (!Number.isFinite(proj.days)) return null;
  const E = proj.days;
  const D = refDailyCost(state, refDef) + eraDollars(state, 0.5, 1860);
  const cons = consumablesEstimate(state, target);
  const boom = clamp(f.boomFactor || 1, 1, 2);
  const repeat = isRepeatClient(op);
  const mult = repRateMult(state.company.reputation) * satisfactionRateMult(op.satisfaction) * boom * (op.major ? 1.12 : 1) * (repeat ? 1.08 : 1);
  const rep = state.company.reputation ?? 30;
  const allowed = TERMS.filter((t) => t !== 'turnkey' || rep >= TURNKEY_MIN_REP);
  const terms = pick(state, allowed);
  const base = D * E + cons;
  let rate, consumablesCap = 0, refValue;
  if (terms === 'dayrate') {
    rate = niceMoney(D * range(state, 1.35, 1.75) * mult);
    consumablesCap = niceMoney(cons * range(state, 0.8, 1.2));
    refValue = rate * E + Math.min(consumablesCap, cons);
  } else if (terms === 'footage') {
    rate = niceMoney((base / target) * range(state, 1.3, 1.7) * mult);
    refValue = rate * target;
  } else {
    rate = niceMoney(base * range(state, 1.6, 2.1) * mult);
    refValue = rate;
  }
  const deadlineDays = Math.ceil(E * range(state, 1.6, 2.0)) + 6;
  const penaltyMult = op.major ? 1.5 : 1;
  const offer = {
    id: nid(C, 'co'), operatorId: op.id, operatorName: op.name, major: op.major, repeat,
    fieldId, plot: plotIdx, targetDepth: target,
    req: { rigType, minMaxDepth: target, offshore: false },
    terms, rate, consumablesCap,
    refDays: Math.round(E * 10) / 10, refValue: niceMoney(refValue), refRigId: refDef.id,
    deadlineDays, earlyDays: Math.max(2, Math.ceil(E)),
    bonus: niceMoney(refValue * range(state, 0.08, 0.18)),
    penalty: niceMoney(refValue * range(state, 0.25, 0.4) * penaltyMult),
    latePerDay: niceMoney(Math.max(refValue * 0.03, 0.05)),
    postedDay: state.day, expiresDay: state.day + BOARD_DAYS - 1, charter: false,
  };
  return offer;
}

// Era 3+: offshore day-rate charters for owners of offshore rigs (needs an offshore field to exist).
function makeCharter(state) {
  const era = eraForDay(state.day);
  if ((era?.num ?? 1) < 3) return null;
  const own = Object.values(state.rigs).map((r) => eqDef(r.defId)).filter((d) => d && stat(d, 'offshore', false));
  if (!own.length) return null;
  const fids = openFieldIds(state).filter((fid) => (fieldDef(fid).waterDepthFt ?? 0) > 0 && freePlots(state, fid).length);
  if (!fids.length) return null;
  const d = own.sort((a, b) => rigStats(b).maxDepth - rigStats(a).maxDepth)[0];
  // the whole offer (target, estimate, deadline) comes from the offshore field — before, a land offer was
  // re-pointed offshore and kept its shallow onshore target and timings
  const o = makeOffer(state, { fitDef: d, fieldIds: fids });
  if (!o) return null;
  o.terms = 'dayrate';
  o.req = { rigType: null, minMaxDepth: o.targetDepth, offshore: true };
  o.rate = niceMoney(refDailyCost(state, d) * range(state, 3.5, 5));
  o.consumablesCap = niceMoney(consumablesEstimate(state, o.targetDepth));
  o.refValue = niceMoney(o.rate * o.refDays);
  o.charter = true;
  return o;
}

export function refreshBoard(state) {
  BOARD_BATCH = { state, free: new Map(), open: null, rw: null };
  try { return refreshBoardImpl(state); } finally { BOARD_BATCH = null; }
}
function refreshBoardImpl(state) {
  const C = initContracts(state);
  ensureOperators(state);
  const n = boardSize(state);
  const owned = [...new Set(Object.values(state.rigs).map((r) => r.defId))].map(eqDef).filter((d) => d && !stat(d, 'offshore', false))
    .sort((a, b) => rigStats(b).maxDepth - rigStats(a).maxDepth).slice(0, 2);
  const board = [];
  for (let i = 0; i < n; i++) {
    const o = makeOffer(state, { fitDef: owned[i] ?? null });
    if (o) board.push(o);
  }
  const ch = makeCharter(state);
  if (ch) board.push(ch);
  C.board = board;
  C.lastRefreshDay = state.day;
  C.nextRefreshDay = state.day + BOARD_DAYS;
  if (board.length) {
    const best = [...board].sort((a, b) => b.refValue - a.refValue)[0];
    notify(state, 'contractOffer', { count: board.length, offerId: best.id, text: `${board.length} drilling contract${board.length > 1 ? 's' : ''} posted — best: ${best.operatorName}, ${fmt$(best.refValue)} at ${fieldDef(best.fieldId)?.name}.`, level: 'info' });
  }
  return board;
}

// ---- eligibility & projections --------------------------------------------------------------
export function contractOfRig(state, rigUid) {
  const C = state.contracts;
  if (!C) return null;
  return Object.values(C.active).find((c) => c.rigUid === rigUid) ?? null;
}

export function rigIneligible(state, rig, offer) {
  const def = eqDef(rig.defId);
  const rs = rigStats(def);
  if (contractOfRig(state, rig.uid)) return 'already on contract';
  if (rig.charterOut) return 'out on charter';
  if (rig.mothballed) return 'in storage (mothballed)';
  if (rig.stacked) return 'stacked';
  const w = rig.wellId ? state.wells[rig.wellId] : null;
  if (w && (ACTIVE_STATUSES.has(w.status) || w.lift?.usesRig)) return `busy at ${w.name}`;
  if (rs.maxDepth < offer.req.minMaxDepth) return `only reaches ${rs.maxDepth.toLocaleString('en-US')} ft`;
  if (offer.req.rigType && rs.rigType !== offer.req.rigType) return `operator wants a ${offer.req.rigType} rig`;
  if (offer.req.offshore && !stat(def, 'offshore', false)) return 'needs an offshore rig';
  if (!offer.req.offshore && stat(def, 'offshore', false)) return "offshore rigs don't drill land wells";
  if (rs.needsEngine && !rig.engineUid) return 'needs an engine';
  if (!activeRigCrew(state, rig).some((c) => c.role === 'driller')) return 'no driller on the crew';
  if (!rig.bitUid && !bestSpareBit(state, rig)) return 'no bit';
  return null;
}

function rigDailyCost(state, rig) {
  let c = 0;
  for (const m of activeRigCrew(state, rig)) c += crewWage(state, m);
  if (rig.engineUid && state.items[rig.engineUid]) {
    const e = eqDef(state.items[rig.engineUid].defId);
    c += eraDollars(state, stat(e, 'fuelPerDay', 0), e.baseYear ?? e.year ?? 1859);
  }
  return c + (rig.safety?.budget ?? 0);
}

// Projected drilling of `offer` with `rig` (uses the live ROP model: crew, bit, engine, WOB, R&D).
export function projectForRig(state, rig, offer) {
  const fdef = fieldDef(offer.fieldId);
  const plot = state.fields[offer.fieldId]?.plots[offer.plot];
  if (!fdef || !plot) return { days: Infinity, ft: 0, avgRop: 0, estimated: true };
  const strata = strataAt(fdef, plot);
  const rs = rigStats(eqDef(rig.defId));
  const curWell = rig.wellId ? state.wells[rig.wellId] : null;
  const fake = { rigId: rig.uid, wob: curWell?.wob ?? 1, shoe: 1e9, deviationDeg: 0 };
  let estimated = false;
  let ropFn = (s) => ropAt(state, fake, s);
  if (!(ropFn(strata[0] ?? { hard: 3 }) > 0)) {
    estimated = true;
    const power = rs.needsEngine ? (rig.engineUid ? stat(eqDef(state.items[rig.engineUid]?.defId), 'powerMult', 1) : refPower(state, eqDef(rig.defId))) : (rs.powerMult ?? 0.6);
    ropFn = (s) => rs.baseRop * power * (HARD_TUNE / (1 + Math.max(1, s.hard ?? 3))) * REF_EFFICIENCY;
  }
  const bit = rig.bitUid ? state.items[rig.bitUid] : bestSpareBit(state, rig);
  const life = bit ? bitStats(eqDef(bit.defId)).life : refBitLife(state, eqDef(rig.defId));
  const target = Math.min(offer.targetDepth, rs.maxDepth);
  const p = projectDrill(strata, target, ropFn, { bitLife: life, rigUpDays: rs.rigUpDays });
  // contingency: casing runs, gas blows, cleanouts and the odd fishing job
  return { ...p, days: Number.isFinite(p.days) ? p.days * 1.12 + 2 : p.days, estimated };
}

// Economics of taking `offer` with `rig`: gross, your costs, net, and a ±30% ROP risk band.
export function rigOption(state, rig, offer) {
  const reason = rigIneligible(state, rig, offer);
  const proj = projectForRig(state, rig, offer);
  const daily = rigDailyCost(state, rig);
  const cons = consumablesEstimate(state, offer.targetDepth);
  const at = (days) => {
    const gross = contractValue(offer, { days, ft: proj.ft, consumables: cons });
    const cost = daily * days + cons;
    return { days, gross, cost, net: gross - cost };
  };
  const finite = Number.isFinite(proj.days);
  const mid = finite ? at(proj.days) : { days: Infinity, gross: 0, cost: Infinity, net: -Infinity };
  return {
    rigUid: rig.uid, name: rig.name, defId: rig.defId, eligible: !reason, reason,
    rop: proj.avgRop, estimated: proj.estimated, dailyCost: daily, consumables: cons,
    ...mid, perDay: finite ? mid.net / proj.days : -Infinity,
    slow: finite ? at(proj.days / 0.7) : null,   // ROP −30%
    fast: finite ? at(proj.days / 1.3) : null,   // ROP +30%
  };
}

// ---- actions --------------------------------------------------------------------------------
export function acceptContract(state, offerId, rigUid) {
  const C = initContracts(state);
  const offer = C.board.find((o) => o.id === offerId);
  if (!offer) return no('That offer is no longer on the board.');
  if (state.day > offer.expiresDay) return no('That offer has expired.');
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (offer.terms === 'turnkey' && (state.company.reputation ?? 0) < TURNKEY_MIN_REP) return no(`Turnkey work needs reputation ${TURNKEY_MIN_REP}+.`);
  const why = rigIneligible(state, rig, offer);
  if (why) return no(`${rig.name}: ${why}.`);
  const op = C.operators[offer.operatorId];
  if (!op) return no('That operator has gone out of business.');
  const f = state.fields[offer.fieldId];
  let plotIdx = offer.plot;
  if (!isFree(f?.plots[plotIdx])) {
    const alt = freePlots(state, offer.fieldId);
    if (!alt.length) return no(`${op.name} lost the lease — no free plot at ${fieldDef(offer.fieldId)?.name}.`);
    plotIdx = alt[0];
  }
  if (rig.wellId) releaseRigImpl(state, rig); // leaving a finished well of yours
  if (!rig.bitUid) {
    const b = bestSpareBit(state, rig);
    if (b) { b.loc = { type: 'rig', id: rig.uid }; rig.bitUid = b.uid; }
  }
  const cid = nid(C, 'k');
  const well = createWell(state, rig, offer.fieldId, plotIdx, offer.targetDepth);
  op.wells = (op.wells ?? 0) + 1;
  well.name = `${op.short} No. ${op.wells}`;
  well.ownerOperatorId = op.id;
  well.contractId = cid;
  rig.at = { fieldId: offer.fieldId, plot: plotIdx };
  rig.wellId = well.id;
  rig.contractId = cid;
  well.bitUid = rig.bitUid;
  startRigUp(state, well, rig);
  C.active[cid] = {
    id: cid, offer: { ...offer, plot: plotIdx }, operatorId: op.id, rigUid, wellId: well.id,
    startDay: state.day, deadlineDay: state.day + offer.deadlineDays,
    earned: 0, reimbursed: 0, paidFt: 0, costMark: well.cost ?? 0, penalties: 0,
    satisfaction: op.satisfaction, incidents: 0, deaths: 0, gushed: false,
  };
  C.board = C.board.filter((o) => o.id !== offerId);
  log(state, `${rig.name} contracted to ${op.name}: ${well.name} at ${fieldDef(offer.fieldId)?.name}, ${Math.round(offer.targetDepth)} ft, ${TERM_LABEL[offer.terms]} ${describeTerms(offer)}.`, 'info', { wellId: well.id });
  return ok(`${rig.name} contracted to ${op.name} (${TERM_LABEL[offer.terms]} ${describeTerms(offer)}). Rigging up on ${well.name}.`, { contractId: cid, wellId: well.id });
}

export function abandonContract(state, contractId) {
  const C = state.contracts;
  const c = C?.active?.[contractId];
  if (!c) return no('No such contract.');
  const pen = c.offer.penalty;
  fail(state, c, 'abandoned', `You walked off ${state.wells[c.wellId]?.name ?? 'the job'} — ${C.operators[c.operatorId]?.name ?? 'the operator'} charges a ${fmt$(pen)} penalty.`);
  return ok(`Contract abandoned. Penalty ${fmt$(pen)}.`);
}

export function acceptTip(state, tipId) {
  const C = state.contracts;
  const t = C?.tips?.find((x) => x.id === tipId);
  if (!t) return no('That tip has gone cold.');
  if (state.day > t.expiresDay) return no('The landowner has already signed with someone else.');
  const p = state.fields[t.fieldId]?.plots[t.plot];
  if (!isFree(p)) { C.tips = C.tips.filter((x) => x !== t); return no('Someone beat you to that plot.'); }
  const full = leasePrice(state, t.fieldId, t.plot);
  const price = Math.round(full * (1 - t.discount));
  if (state.company.cash < price) return no(`The lease costs ${fmt$(price)} even with the discount.`);
  const refund = full - price;
  state.company.cash += refund; // lease at full price, then the landowner's discount comes back
  const r = leaseImpl(state, t.fieldId, t.plot);
  if (!r.ok) { state.company.cash -= refund; return r; }
  book(state, 'leases', -refund);
  p.leasePaid = price;
  C.tips = C.tips.filter((x) => x !== t);
  return ok(`Leased plot ${t.plot + 1} at ${fieldDef(t.fieldId)?.name} on your driller's tip for ${fmt$(price)} (${Math.round(t.discount * 100)}% off).`);
}

export function dismissTip(state, tipId) {
  const C = state.contracts;
  if (!C?.tips?.some((x) => x.id === tipId)) return no('No such tip.');
  C.tips = C.tips.filter((x) => x.id !== tipId);
  return ok('Tip ignored.');
}

// ---- daily ----------------------------------------------------------------------------------
export function tickContracts(state) {
  const C = initContracts(state);
  if (state.day >= CONTRACTS_OPEN_DAY && state.day >= C.nextRefreshDay) refreshBoard(state);
  for (const c of Object.values(C.active)) tickOne(state, c);
  if (C.tips.length) C.tips = C.tips.filter((t) => t.expiresDay >= state.day);
  tickHandedOver(state);
  if (dayToDate(state.day).dom === 1) {
    for (const op of Object.values(C.operators)) op.satisfaction = Math.round((op.satisfaction + (50 - op.satisfaction) * 0.02) * 10) / 10;
  }
}

function pay(state, c, amount) {
  if (!(amount > 0)) return;
  earn(state, amount, 'revenue');
  c.earned += amount;
  state.contracts.stats.earned += amount;
}

function tickOne(state, c) {
  const well = state.wells[c.wellId];
  const rig = state.rigs[c.rigUid];
  if (!well) return fail(state, c, 'lost', 'The contract well was lost.');
  if (!rig || rig.wellId !== well.id) return fail(state, c, 'abandoned', `${rig?.name ?? 'Your rig'} left ${well.name} before reaching TD.`);
  if (well.status === 'PLUGGED') return fail(state, c, 'abandoned', `${well.name} was plugged before TD.`);
  if (DONE_STATUSES.has(well.status)) return onWellTD(state, well); // fallback if drilling.js has no TD hook
  const o = c.offer;
  if (o.terms === 'dayrate') {
    // The operator pays for a working rig only: nothing while blocked (no bit/crew/rope, flooded, paused)
    // or after the deadline, half rate while fishing for your own lost tools.
    const f = dayRateFactor(well, state.day <= c.deadlineDay);
    if (f > 0) { pay(state, c, o.rate * f); c.paidDays = (c.paidDays ?? 0) + f; }
    else c.standbyDays = (c.standbyDays ?? 0) + 1;
    const d = Math.max(0, (well.cost ?? 0) - c.costMark);
    const r = Math.min(d, Math.max(0, (o.consumablesCap ?? 0) - c.reimbursed));
    if (r > 0) { pay(state, c, r); c.reimbursed += r; }
  } else if (o.terms === 'footage') {
    const reach = Math.min(well.depth, o.targetDepth);
    if (reach > c.paidFt) { pay(state, c, (reach - c.paidFt) * o.rate); c.paidFt = reach; }
  }
  c.costMark = well.cost ?? 0;
  if (well.status === 'GUSHER' && !c.gushed) {
    c.gushed = true;
    const good = (eraForDay(state.day)?.num ?? 1) <= 2;
    c.satisfaction = clamp(c.satisfaction + (good ? 8 : -10), 0, 100);
  }
  scanIncidents(state, c, well);
  if (state.day > c.deadlineDay) {
    c.satisfaction = clamp(c.satisfaction - 0.5, 0, 100);
    if (state.day > c.deadlineDay + Math.ceil(o.deadlineDays * LATE_GRACE)) {
      return fail(state, c, 'late', `${C_op(state, c)?.name ?? 'The operator'} lost patience with ${well.name} — contract terminated, ${fmt$(o.penalty)} penalty.`);
    }
  }
}
const C_op = (state, c) => state.contracts.operators[c.operatorId];

// Share of the day rate earned today (exported for tests/UI).
export function dayRateFactor(well, beforeDeadline = true) {
  if (!beforeDeadline || !well) return 0;
  if (well.status === 'PAUSED' || well.status === 'SUSPENDED' || (well.status === 'DRILLING' && well.problem)) return 0;
  if (well.status === 'FISHING') return 0.5;
  return 1;
}

// Accidents & mishaps logged today on the contract well (and deaths in the memorial) hurt the client.
function scanIncidents(state, c, well) {
  let n = 0;
  for (let i = state.log.length - 1; i >= 0; i--) {
    const e = state.log[i];
    if (e.day !== state.day) break;
    if (e.wellId === well.id && (e.level === 'warn')) n++;
  }
  const deaths = (state.memorial ?? []).filter((m) => m.day === state.day && m.wellId === well.id).length;
  if (n) {
    c.incidents += n;
    c.satisfaction = clamp(c.satisfaction - 4 * n, 0, 100);
    state.company.reputation = Math.max(0, state.company.reputation - Math.min(3, n));
  }
  if (deaths) {
    c.deaths += deaths;
    c.satisfaction = clamp(c.satisfaction - 15 * deaths, 0, 100);
  }
}

// Operator production from handed-over wells drains the shared reservoir (rule of capture).
function tickHandedOver(state) {
  for (const w of allWells(state)) {
    const p = w.opProd;
    if (!p || w.status !== 'HANDED_OVER') continue;
    if (state.day - p.startDay > HANDOVER_KEEP_DAYS) { w.opProd = null; continue; }
    const res = state.fields[w.fieldId]?.reservoirs?.[p.resId];
    if (!res || res.remaining <= 0) continue;
    const yrs = (state.day - p.startDay) / 365;
    const q = p.qi / Math.pow(1 + p.b * p.di * yrs, 1 / p.b) * Math.sqrt(Math.max(0, res.remaining) / Math.max(1, res.initial)) * (res.pressure < 0.55 ? 0.3 : 1);
    res.remaining = Math.max(0, res.remaining - q);
    p.rate = Math.round(q * 10) / 10;
  }
}

function handOver(state, c, well, struck) {
  if (!well) return;
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  if (rig) { releaseRigImpl(state, rig); delete rig.contractId; }
  if (well.timerId) { cancelTimer(state, well.timerId); well.timerId = null; }
  const rigC = state.rigs[c.rigUid];
  if (rigC) delete rigC.contractId;
  well.status = 'HANDED_OVER';
  well.problem = null;
  well.handedOverDay = state.day;
  well.rigId = null;
  if (struck && well.reservoirId) {
    const fdef = fieldDef(well.fieldId);
    const plot = state.fields[well.fieldId].plots[well.plot];
    const r = reservoirsAt(fdef, plot).find((x) => x.id === well.reservoirId);
    if (r) {
      const [lo, hi] = r.qiRange ?? [5, 30];
      const qi = (lo + (hi - lo) * (plot.truth?.qiRoll ?? 0.5)) * 0.7 * (well.gusher ? 1.5 : 1);
      well.opProd = { resId: r.id, qi: Math.round(qi * 10) / 10, di: r.di ?? 0.6, b: r.b ?? 0.5, startDay: state.day, rate: 0 };
    }
  }
}

function rollTip(state, c, well) {
  const C = state.contracts;
  const rig = state.rigs[c.rigUid];
  const driller = rig ? activeRigCrew(state, rig).find((x) => x.role === 'driller') : null;
  const p = TIP_CHANCE_MIN + (TIP_CHANCE_MAX - TIP_CHANCE_MIN) * clamp((driller?.skill ?? 40) / 100, 0, 1);
  if (!chance(state, p)) return null;
  const f = state.fields[well.fieldId];
  const p0 = f.plots[well.plot];
  const nb = [];
  f.plots.forEach((q, i) => { if (q !== p0 && isFree(q) && Math.max(Math.abs(q.x - p0.x), Math.abs(q.y - p0.y)) <= 1) nb.push(i); });
  if (!nb.length) return null;
  const good = nb.filter((i) => (f.plots[i].truth?.prospectIdx ?? -1) >= 0);
  const plotIdx = good.length && chance(state, 0.85) ? pick(state, good) : pick(state, nb);
  const full = leasePrice(state, well.fieldId, plotIdx);
  const op = C_op(state, c);
  const who = driller?.name ?? 'Your driller';
  const tip = {
    id: nid(C, 'tip'), fieldId: well.fieldId, plot: plotIdx, discount: TIP_DISCOUNT,
    fullPrice: full, price: Math.round(full * (1 - TIP_DISCOUNT)), day: state.day, expiresDay: state.day + TIP_DAYS,
    operatorName: op?.name ?? 'the operator', wellName: well.name, drillerName: who,
    text: `${who} saw a show of oil in the cuttings at ${well.name} and swears the sand runs under plot ${plotIdx + 1}. The farmer will lease it quietly at ${Math.round(TIP_DISCOUNT * 100)}% off.`,
  };
  C.tips.push(tip);
  return tip;
}

// Call when a contract well reaches TD (drilling.js hook) — or detected by tickContracts.
export function onWellTD(state, well) {
  const C = state.contracts;
  if (!C) return;
  const c = Object.values(C.active).find((x) => x.wellId === well.id);
  if (!c) return;
  const o = c.offer;
  const op = C_op(state, c);
  if (o.terms === 'footage') {
    const reach = Math.min(well.depth, o.targetDepth);
    if (reach > c.paidFt) { pay(state, c, (reach - c.paidFt) * o.rate); c.paidFt = reach; }
  } else if (o.terms === 'turnkey') pay(state, c, o.rate);
  else if (o.terms === 'dayrate') {
    const d = Math.max(0, (well.cost ?? 0) - c.costMark);
    const r = Math.min(d, Math.max(0, (o.consumablesCap ?? 0) - c.reimbursed));
    if (r > 0) { pay(state, c, r); c.reimbursed += r; }
  }
  const s = settleTerms(o, { day: state.day, startDay: c.startDay, deadlineDay: c.deadlineDay });
  pay(state, c, s.bonus);
  if (s.latePenalty > 0) { spend(state, s.latePenalty, 'other', false); c.penalties += s.latePenalty; C.stats.penalties += s.latePenalty; c.earned -= s.latePenalty; }
  const struck = !!well.reservoirId;
  const dSat = satisfactionDelta({ early: s.early, lateDays: s.lateDays, struck, incidents: 0, deaths: 0 }); // incidents already applied daily
  c.satisfaction = clamp(c.satisfaction + dSat, 0, 100);
  if (op) {
    op.satisfaction = Math.round(clamp(op.satisfaction + (c.satisfaction - op.satisfaction) * 0.6 + dSat * 0.4, 0, 100) * 10) / 10;
    op.completed = (op.completed ?? 0) + 1;
  }
  state.company.reputation = Math.min(100, state.company.reputation + 1 + (s.early && !c.incidents ? 1 : 0));
  C.stats.completed++;
  const tip = rollTip(state, c, well);
  handOver(state, c, well, struck);
  const days = state.day - c.startDay;
  C.history.unshift({ id: c.id, operatorId: c.operatorId, operatorName: op?.name ?? o.operatorName, wellId: well.id, wellName: well.name, fieldId: well.fieldId, terms: o.terms, outcome: 'done', day: state.day, days, earned: Math.round(c.earned * 100) / 100, early: s.early, lateDays: s.lateDays, struck, depth: Math.round(well.depth), satisfaction: Math.round(c.satisfaction) });
  if (C.history.length > 60) C.history.length = 60;
  delete C.active[c.id];
  const msg = `${well.name} reached TD at ${Math.round(well.depth)} ft for ${op?.name ?? 'the operator'}${struck ? ' — and struck oil!' : '.'} Contract earned ${fmt$(c.earned)}${s.early ? ' including an early-finish bonus' : s.lateDays ? `, less ${fmt$(s.latePenalty)} for ${s.lateDays} days late` : ''}.`;
  notify(state, 'contractDone', { contractId: c.id, operatorId: c.operatorId, operatorName: op?.name ?? o.operatorName, wellId: well.id, fieldId: well.fieldId, terms: o.terms, earned: c.earned, days, early: s.early, lateDays: s.lateDays, struck, depth: well.depth, tip: tip ? { id: tip.id, plot: tip.plot, fieldId: tip.fieldId, price: tip.price } : null, text: msg, level: 'good' });
  if (tip) notify(state, 'news', { kind: 'tip', fieldId: tip.fieldId, text: `Tip: ${tip.text}`, level: 'good' });
}

function fail(state, c, reason, text) {
  const C = state.contracts;
  const o = c.offer;
  const op = C_op(state, c);
  const well = state.wells[c.wellId];
  const pen = reason === 'lost' ? 0 : o.penalty;
  if (pen > 0) { spend(state, pen, 'other', false); C.stats.penalties += pen; }
  if (op) { op.satisfaction = clamp(op.satisfaction - 30, 0, 100); op.failed = (op.failed ?? 0) + 1; }
  state.company.reputation = Math.max(0, state.company.reputation - 3);
  C.stats.failed++;
  if (reason === 'abandoned') C.stats.abandoned++;
  const rig = state.rigs[c.rigUid];
  if (rig) delete rig.contractId;
  if (well) handOver(state, c, well, false);
  C.history.unshift({ id: c.id, operatorId: c.operatorId, operatorName: op?.name ?? o.operatorName, wellId: c.wellId, wellName: well?.name ?? '', fieldId: o.fieldId, terms: o.terms, outcome: reason, day: state.day, days: state.day - c.startDay, earned: Math.round((c.earned - pen) * 100) / 100, penalty: pen, depth: Math.round(well?.depth ?? 0) });
  if (C.history.length > 60) C.history.length = 60;
  delete C.active[c.id];
  notify(state, 'contractFailed', { contractId: c.id, operatorId: c.operatorId, operatorName: op?.name ?? o.operatorName, wellId: c.wellId, fieldId: o.fieldId, reason, penalty: pen, text, pause: true, level: 'warn' });
}

// ---- selector -------------------------------------------------------------------------------
// Pure view for the Contracts panel (never mutates state).
export function contractsView(state) {
  const C = state.contracts;
  const era = eraForDay(state.day);
  const base = {
    open: state.day >= CONTRACTS_OPEN_DAY, opensDay: CONTRACTS_OPEN_DAY,
    flavor: BOARD_FLAVOR[era?.num ?? 1] ?? BOARD_FLAVOR[1],
    reputation: state.company.reputation, turnkeyMinRep: TURNKEY_MIN_REP,
  };
  if (!C) return { ...base, board: [], active: [], tips: [], operators: [], history: [], stats: { completed: 0, failed: 0, abandoned: 0, earned: 0, penalties: 0 }, nextRefreshDay: Math.max(state.day, CONTRACTS_OPEN_DAY) };
  const rigs = Object.values(state.rigs);
  const opView = (id) => {
    const op = C.operators[id];
    return op ? { id: op.id, name: op.name, short: op.short, major: op.major, satisfaction: op.satisfaction, completed: op.completed, failed: op.failed, repeat: isRepeatClient(op), regionId: op.regionId } : null;
  };
  const board = C.board.map((o) => {
    const options = rigs.map((r) => rigOption(state, r, o));
    const elig = options.filter((x) => x.eligible && Number.isFinite(x.days)).sort((a, b) => b.perDay - a.perDay);
    return { ...o, operator: opView(o.operatorId), fieldName: fieldDef(o.fieldId)?.name ?? o.fieldId, termsLabel: TERM_LABEL[o.terms], termsText: describeTerms(o), rigs: options, best: elig[0] ?? null, daysLeft: o.expiresDay - state.day };
  });
  const active = Object.values(C.active).map((c) => {
    const w = state.wells[c.wellId];
    const rig = state.rigs[c.rigUid];
    const timer = w?.timerId ? state.timers[w.timerId] : null;
    return {
      id: c.id, offer: c.offer, operator: opView(c.operatorId), fieldName: fieldDef(c.offer.fieldId)?.name ?? '',
      termsLabel: TERM_LABEL[c.offer.terms], termsText: describeTerms(c.offer),
      wellId: c.wellId, wellName: w?.name ?? '?', status: w?.status ?? 'LOST', problem: w?.problem ?? null,
      depth: w?.depth ?? 0, target: c.offer.targetDepth, progress: w ? clamp(w.depth / Math.max(1, c.offer.targetDepth), 0, 1) : 0,
      rigUid: c.rigUid, rigName: rig?.name ?? '?', startDay: c.startDay, deadlineDay: c.deadlineDay,
      daysElapsed: state.day - c.startDay, daysLeft: c.deadlineDay - state.day, late: state.day > c.deadlineDay,
      failDay: c.deadlineDay + Math.ceil(c.offer.deadlineDays * LATE_GRACE),
      earlyBy: c.startDay + c.offer.earlyDays,
      earned: c.earned, reimbursed: c.reimbursed, satisfaction: c.satisfaction, incidents: c.incidents, deaths: c.deaths,
      penalty: c.offer.penalty,
      timer: timer ? { id: timer.id, kind: timer.kind, daysLeft: timer.daysLeft, rushCost: rushCost(state, timer) } : null,
    };
  });
  return {
    ...base, board, active,
    tips: C.tips.map((t) => ({ ...t, fieldName: fieldDef(t.fieldId)?.name ?? t.fieldId, priceNow: Math.round(leasePrice(state, t.fieldId, t.plot) * (1 - t.discount)), daysLeft: t.expiresDay - state.day })),
    operators: Object.keys(C.operators).map(opView).sort((a, b) => b.satisfaction - a.satisfaction),
    history: C.history, stats: { ...C.stats }, nextRefreshDay: C.nextRefreshDay, lastRefreshDay: C.lastRefreshDay,
  };
}
